import { Timestamp, doc, getDoc, setDoc, query, collection, onSnapshot, updateDoc } from 'firebase/firestore'
import { defineStore } from 'pinia'
import pinia from '@/store'
import { callBackend, db, formatErrorMessageCallBackend } from '@/plugins/firebase'
import { useUserStore } from '@/store/userStore.js'
import { useVendorStore } from '@/store/vendorStore.js'
import { useDynamicFormStore } from '@/store/dynamicFormStore'
import { useProjectUsersStore } from '@/store/projectUsersStore'
import { useProductStore } from '@/store/productStore'
import getAmountTextFormatCurrency from '@/shared/utils/getAmountTextFormatCurrency.js'
import getInfoDevice from '@/shared/utils/getInfoDevice.js'
import translation from '@/shared/utils/translation.js'
import { getTypePriceOption, getEndBillingDate } from '@/shared/utils/payment'
import { formatDate } from '@/shared/utils/dateFilter'
import { getChildDedupKey } from '@/shared/utils/getDedupKey.js'
import i18n from '@/plugins/i18n'
import { resolveProvider } from '@/shared/payments/paymentClientFactory'

const vendorStore = useVendorStore(pinia)
const dynamicFormStore = useDynamicFormStore(pinia)
const userStore = useUserStore(pinia)
const productStore = useProductStore(pinia)

export const useProjectStore = defineStore('projectStore', {
  state: () => ({
    project: null,
    form: {},
    paymentMethod: null,
    waiver: [],
    staticContentWaiver: [],
    terms: [],
    selectedShippingRate: null,
    priceOptionId: null,
    price: null,
    paymentInfo: null,
    paymentUserId: null,
    customBilling: null,
    paymentProcessStarted: false,
  }),
  getters: {
    timezone: (state) => state.project?.timezone,
    formatPrice: (state) => (price) => price !== null && price !== undefined ? `${getAmountTextFormatCurrency(price.toFixed(2), vendorStore.currency)}` : null,
    isProjectFree: (state) => !state.project?.features?.price,
    hasDifferentPaymentOptions: (state) => state.project?.pricing?.priceOptions?.length > 1,
    productInscriptions: (state) => state.form.players.filter((p) => !p.isExtra),
    productsExtraInForm: (state) => state.form.players.filter((p) => p.isExtra),
    isSubscriberApprovalRequired: state => state.project?.subscriberApproval?.required,
    
    // Getters para los stepId de inscripción
    stepId1: () => {
      const dynamicFormStore = useDynamicFormStore(pinia)
      return dynamicFormStore.dataUser.parentProfileFields ? 1 : null
    },
    
    stepId2: () => {
      const dynamicFormStore = useDynamicFormStore(pinia)
      return dynamicFormStore.dataUser.parentProfileFields ? 2 : 1
    },
    
    stepId3: () => {
      const dynamicFormStore = useDynamicFormStore(pinia)
      const productStore = useProductStore(pinia)
      if (!productStore.areProductsExtra) return null
      return dynamicFormStore.dataUser.parentProfileFields ? 3 : 2
    },
    
    stepId4: (state) => {
      const dynamicFormStore = useDynamicFormStore(pinia)
      const productStore = useProductStore(pinia)
      
      // Si no hay waiver, este step no existe
      if (!state.project?.features?.waiver) return null
      
      // Si hay datos padre, el waiver es step 4
      if (dynamicFormStore.dataUser.parentProfileFields) {
        return productStore.areProductsExtra ? 4 : 3
      }
      
      // Si no hay datos padre, el waiver es step 3
      return productStore.areProductsExtra ? 3 : 2
    },
    
    stepId5: (state) => {
      const dynamicFormStore = useDynamicFormStore(pinia)
      const productStore = useProductStore(pinia)
      
      // Si no hay opciones de pago diferentes, este step no existe
      if (!state.hasDifferentPaymentOptions || state.isProjectFree) return null
      
      // Si hay datos padre
      if (dynamicFormStore.dataUser.parentProfileFields) {
        if (productStore.areProductsExtra) {
          return state.project?.features?.waiver ? 5 : 4
        } else {
          return state.project?.features?.waiver ? 4 : 3
        }
      }
      
      // Si no hay datos padre
      if (productStore.areProductsExtra) {
        return state.project?.features?.waiver ? 4 : 3
      } else {
        return state.project?.features?.waiver ? 3 : 2
      }
    },
    
    stepId6: (state) => {
      const dynamicFormStore = useDynamicFormStore(pinia)
      const productStore = useProductStore(pinia)

      const vendor = vendorStore.vendor
      const provider = resolveProvider(vendor)
      const hasProviderAccount = !!vendor?.[provider]?.accountId
      if (!hasProviderAccount) return null
      if (state.isProjectFree && !productStore.areProductsExtra) return null

      // El step 5 (elegir tipo de pago) solo existe si hay opciones de pago
      // diferentes Y el proyecto NO es gratis (ver getter stepId5). stepId6 debe
      // espejar esa condición para no dejar un hueco en la numeración de pasos.
      const hasPaymentTypeStep = state.hasDifferentPaymentOptions && !state.isProjectFree

      // Si hay datos padre
      if (dynamicFormStore.dataUser.parentProfileFields) {
        if (productStore.areProductsExtra) {
          if (state.project?.features?.waiver) {
            return hasPaymentTypeStep ? 6 : 5
          } else {
            return hasPaymentTypeStep ? 5 : 4
          }
        } else {
          if (state.project?.features?.waiver) {
            return hasPaymentTypeStep ? 5 : 4
          } else {
            return hasPaymentTypeStep ? 4 : 3
          }
        }
      }

      // Si no hay datos padre
      if (productStore.areProductsExtra) {
        if (state.project?.features?.waiver) {
          return hasPaymentTypeStep ? 5 : 4
        } else {
          return hasPaymentTypeStep ? 4 : 3
        }
      } else {
        if (state.project?.features?.waiver) {
          return hasPaymentTypeStep ? 4 : 3
        } else {
          return hasPaymentTypeStep ? 3 : 2
        }
      }
    },
    shippingRateIds: (state) =>
      state.form.players
        .flatMap((player) => player.cart)
        .filter((item) => item.type === 'product')
        .reduce((map, product) => product.shippingRateIds, {}),
    shippingFee(state) {
      if (this.shippingRateIds?.length) {
        return state.selectedShippingRate?.price ?? 0
      }
      return 0
    },
  },
  actions: {
    setSelectedShippingRate(shippingRate) {
      this.selectedShippingRate = shippingRate
    },
    setCustomBilling({ startBillingDate, endBillingDate, amountInstallments, installmentCount, installments, isHeterogeneous = false }) {
      const normalizeDate = (date) => {
        if (!date) return null
        if (date.seconds !== undefined) return date
        if (typeof date === 'string') {
          const dateObj = new Date(date)
          return { seconds: Math.floor(dateObj.getTime() / 1000), nanoseconds: 0 }
        }
        return date
      }
      this.customBilling = {
        isHeterogeneous: isHeterogeneous,
        startBillingDate: normalizeDate(startBillingDate),
        endBillingDate: normalizeDate(endBillingDate),
        amountInstallments,
        installmentCount,
        installments: Array.isArray(installments)
          ? installments.map(cuota => ({
              ...cuota,
              dueDate: normalizeDate(cuota.dueDate),
            }))
          : null,
      }
    },
    setForm({ form }) {
      this.form = form
    },
    setPaymentMethod({ paymentMethod }) {
      this.paymentMethod = paymentMethod
    },
    setPaymentProcessStarted(value) {
      this.paymentProcessStarted = value
    },
    setPriceOptionId(priceOptionId) {
      this.priceOptionId = priceOptionId
    },
    setPrice(priceOption) {
      // En proyectos gratis la inscripción no tiene precio: se evita que el monto
      // del priceOption se propague a la línea del carrito y al fee de gestión.
      if (this.isProjectFree) {
        this.price = 0
        return
      }
      this.price = priceOption?.instant?.amount ? priceOption.instant.amount : null
    },
    setPaymentInfo(priceOption) {
      const typePriceOption = getTypePriceOption(priceOption)
      const hasSubscriptionPortion = ['subscription', 'mixed'].includes(typePriceOption)
      const showUniform = hasSubscriptionPortion && !this.customBilling?.isHeterogeneous
      const resolvedAmountInstallments = showUniform ?
      (this.customBilling ? this.customBilling.amountInstallments : priceOption.subscription.amount / priceOption.subscription.installmentCount) : null
      const resolvedInstallmentCount = showUniform ?
      (this.customBilling ? this.customBilling.installmentCount : priceOption.subscription.installmentCount) : null
      const resolvedStartBillingDate = showUniform ?
      (this.customBilling ? this.customBilling.startBillingDate : priceOption.subscription.startBillingDate) : null
      const resolvedEndBillingDate = showUniform ?
      (this.customBilling ? this.customBilling.endBillingDate : getEndBillingDate(priceOption.subscription)) : null
      this.paymentInfo = {
          typePriceOption,
          instantPayment: ['instant', 'mixed'].includes(typePriceOption) ? i18n.global.t(`inscription.form.step5.${typePriceOption === 'mixed' ? 'immediatePayment' : 'unicInmediatePayment'}`) : null,
          amountInstallments: showUniform ?
          i18n.global.t('inscription.form.step5.amountInstallments', {
          installmentCount: resolvedInstallmentCount,
          amount: this.formatPrice(resolvedAmountInstallments),
          }) : null,
          startBillingDate: showUniform ?
          i18n.global.t('inscription.form.step5.startBillingDate', {
          date: formatDate(resolvedStartBillingDate, this.project.timezone),
          }) : null,
          endBillingDate: showUniform ?
          i18n.global.t('inscription.form.step5.endBillingDate', {
          date: formatDate(resolvedEndBillingDate, this.project.timezone),
          }) : null,
      }
    },
    updatePaymentInfoTranslations() {
      if (this.priceOptionId) {
        const priceOption = this.project?.pricing?.priceOptions?.find(option => option.id === this.priceOptionId)
        if (priceOption) this.setPaymentInfo(priceOption)
      }
    },
    async bindProject({ organizationId, projectId }) {

      try {
        const docRef = doc(db, 'properties', organizationId, 'projects', projectId)
        const setSnapshot = () => new Promise((resolve, reject) => {
          onSnapshot(docRef, snap => { 
            this.project = snap.data()
            resolve()
          }, reject)
        })
  
        await setSnapshot()

      } catch(error) {
        console.error('projectStore.js -> bindProject: Error getting project', error)
      }
      
    },
    async bindWaiver({ organizationId, projectId }) {
      const colRef = query(collection(db, 'properties', organizationId, 'projects', projectId, 'waiver'))
      onSnapshot(colRef, (snapshot) => {
        this.waiver = snapshot.docs.map((waiverDoc) => waiverDoc.data())
      })
    },
    async bindTerms({ organizationId, projectId }) {
      const colRef = query(collection(db, 'properties', organizationId, 'projects', projectId, 'terms'))
      onSnapshot(colRef, (snapshot) => {
        this.terms = snapshot.docs.map((termDoc) => termDoc.data())
      })
    },
    async bindStaticContentWaiver() {
      const colRef = query(collection(db, 'staticContent', 'projectWaiver', 'waiver'))
      onSnapshot(colRef, (snapshot) => {
        this.staticContentWaiver = snapshot.docs.map((staticContentWaiverDoc) => staticContentWaiverDoc.data())
      })
    },
    async createDefaultPaymentMethod() {
      const defaultPaymentMethodId = await userStore.createPaymentMethod({
        userId: this.paymentUserId,
        data: this.paymentMethod,
      })
      await userStore.update({
        id: this.paymentUserId,
        data: { defaultPaymentMethodId },
      })
    },
    async createEnrollment({ organizationId, projectId, data }) {
      if (data.info?.form) {
        const storagePath = `users/${data.userId}/membership/subscriber-project-${projectId}/`
        data.info.form = await dynamicFormStore.parseForm({
          form: data.info.form,
          storagePath,
        })
      }
      const enrollmentsRef = doc(db, 'properties', organizationId, 'projects', projectId, 'enrollments', data.id)
      await setDoc(enrollmentsRef, {
        ...data,
        creationSource: 'inscription',
      })
    },
    async getEphemeralKey() {
      try {
        const ephemeralKeyData = await callBackend('payments/get-ephemeral-key', {
          userId: this.paymentUserId,
          currency: vendorStore.currency,
        })
        return { error: null, ephemeralKeyData }
      } catch (error) {
        console.error('projectStore.js -> getEphemeralKey: Error getting ephemeral key', error)
        return formatErrorMessageCallBackend(error)
      }
    },
    async createSetupIntent({ stripePaymentMethodId, offSession }) {
      try { 
        const { clientSecret } = await callBackend('payments/create-setup-intent', {
          userId: this.paymentUserId,
          stripePaymentMethodId: stripePaymentMethodId,
          offSession: offSession,
          currency: vendorStore.currency
        })
        return { error: null, clientSecret }
      } catch (error) {
        console.error('projectStore.js -> createSetupIntent: Error creating setup intent', error)
        return formatErrorMessageCallBackend(error)
      }
    },
    async createOrder({ provider, cart, currency, redirectUrl, cancelUrl, ...rest } = {}) {
      try {
        const order = await callBackend('payments/create-order', {
          userId: this.paymentUserId,
          provider,
          projectId: this.project?.id,
          organizationId: this.project?.organizationId,
          cart,
          currency,
          redirectUrl,
          cancelUrl,
          ...rest
        })
        return { error: null, order }
      } catch (error) {
        console.error('projectStore.js -> createOrder: Error creating order', error)
        return formatErrorMessageCallBackend(error)
      }
    },
    async createEnrollmentAndPaymentItems({ formData, organizationId, projectId }) {
      const projectUsersStore = useProjectUsersStore()

      let parentUser = null
      let parentInfoForm = null

      // 0. Verify that the parent's information is requested on the form and parse dynamic form if exists
      if (dynamicFormStore?.dataUser?.parentProfileFields) {
        const { parentDynamicForm, ...parentData } = formData.parentForm
        parentInfoForm = parentDynamicForm?.length ? this.parseDynamicForm({ dynamicForm: parentDynamicForm }) : null

        // 0.1. Back Create parent user if no exixts. If exists update parent user.
        parentUser = parentData.email ? await userStore.readByEmail(parentData.email) : false

        if (!parentUser) {
          parentUser = { id: null, ...parentData }
          parentUser.id = await userStore.createWithBackend({
            data: parentData,
          })
        } else {
          await userStore.update({ id: parentUser.id, data: parentData })
        }

        // 0.2. Change data of dynamicForm in all players
        if (parentInfoForm) {
          await Promise.all(
            projectUsersStore.players
              .filter((rP) => parentUser.linkedUsers?.children.includes(rP.id))
              .map(async (p) => {
                await userStore.updateProjectMembershipParentFields({
                  id: p.id,
                  role: 'subscriber',
                  entityType: 'project',
                  entityId: projectId,
                  data: { form: { ...parentInfoForm } },
                })
              }),
          )
        }
      }

      // 1. Back Create player user.
      const paymentItems = await Promise.all(
        formData.playerForm.map(async (player) => {
          const { playerDynamicForm, cart, ...playerData } = player
          const playerUser = { id: null }
          const existPlayer = playerData.email ? await userStore.readByEmail(playerData.email) : false
          if (!existPlayer) {
            const dedupKey = !player.email
              ? getChildDedupKey({
                  parentId: parentUser?.id ?? 'noparent',
                  firstName: player.firstName,
                  lastName: player.lastName,
                  birthdate: player.birthdate,
                })
              : null
            const data = { ...playerData, dedupKey }
            playerUser.id = await userStore.createWithBackend({
              data,
            })
          } else {
            playerUser.id = existPlayer.id
            await userStore.update({ id: existPlayer.id, data: playerData })
          }

          if (!userStore.playerId) {
            userStore.setPlayerId({ playerId: playerUser.id })
          }

          // 1.1. Link users if the parent's data is requested
          if (dynamicFormStore?.dataUser?.parentProfileFields) {
            try {
              await callBackend('users/link', {
                user1: { id: parentUser?.id, relationshipRole: 'parent' },
                user2: { id: playerUser.id, relationshipRole: 'child' },
              })
            } catch (error) {
              console.error('projectStore.js -> createEnrollmentAndPaymentItems: Error linking users', error)
              return formatErrorMessageCallBackend(error)
            }
          }

          // 1.2. Create enrollment
          const playerInfoForm = playerDynamicForm?.length
            ? this.parseDynamicForm({ dynamicForm: playerDynamicForm })
            : null
          const enrollment = {
            id: playerUser.id,
            userId: playerUser.id,
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now(),
            status: 'pendingApproval',
            role: 'player',
            lang: translation.currentLocale,
            // hasAcceptedMediaUse,
            enrolledBy: parentUser?.id ?? playerUser?.id,
            ...((parentInfoForm || playerInfoForm) && {
              info: {
                form: {
                  ...(parentInfoForm && { ...parentInfoForm }),
                  ...(playerInfoForm && { ...playerInfoForm }),
                },
              },
            }),
            ...(this.project.features?.waiver && {
              waiverSignatureBase64: formData.waiverForm.signature.replace('data:image/png;base64,', ''),
              waiverName: formData.waiverForm.name,
            }),
            originInscription: {
              platform: 'web',
              device: getInfoDevice(),
            }
          }
          await this.createEnrollment({
            organizationId,
            projectId,
            data: enrollment,
          })
          if (this.isProjectFree && !this.isSubscriberApprovalRequired) {
            try {
              await callBackend('projects/users/approve-enrollment', {
                organizationId,
                projectId,
                enrollmentId: enrollment.id,
                setPassword: true,
              })
            } catch (error) {
              console.error('projectStore.js -> createEnrollmentAndPaymentItems: Error approving enrollment', error)
              return formatErrorMessageCallBackend(error)
            }
          }

          // 1.3. Create payment items
          const cartItems = cart.map((item) => ({
            id: item.id,
            type: item.type,
            name: item.name,
            image: item.image,
            amount: item.amount,
            currency: vendorStore.currency || null,
            vendorId: vendorStore.vendor?.id || null,
            units: 1,
            organizationId,
            projectId,
            recipientUserId: playerUser.id,
            enrollmentId: enrollment.id,
            createdAt: Timestamp.now(),
            ...(item.type === 'project' && this.priceOptionId && { priceOptionId: this.priceOptionId }),
          }))
          return cartItems
        }),
      )
      this.paymentUserId = parentUser?.id ?? userStore.playerId
      if ((this.isProjectFree && !productStore.areProductsExtra) || (this.isProjectFree && !formData.productsExtra)) return { error: null }
      
      // 3. Add products extra in user cart
      const productsExtra = Object.values(
        formData.productsExtra.reduce((acc, p) => {
          if (!acc[p.id]) {
            acc[p.id] = {
              id: p.id,
              archetypeId: p.archetypeId,
              type: p.type,
              name: p.name,
              image: p.image,
              amount: 0,
              vendorProductId: p.vendorProductId ?? null,
              currency: vendorStore.currency,
              units: 0,
              organizationId,
              projectId,
              recipientUserId: this.paymentUserId,
              ...(this.selectedShippingRate && {
                shippingRateId: this.selectedShippingRate?.id,
              }),
            }
          }
          acc[p.id].units += 1
          acc[p.id].amount += p.amount
          return acc
        }, {}),
      )
      const allItems = [
        ...(this.isProjectFree && productStore.areProductsExtra ? [] : paymentItems.flatMap((items) => items)),
        ...productsExtra
      ]
      console.log('allItems', allItems)
      // 4. Create address for shipping and includes in user cart
      let shippingAddressId = null
      if (formData.shippingAddressForm) {
        shippingAddressId = await userStore.createAddress({
          userId: this.paymentUserId,
          data: formData.shippingAddressForm,
        })
      }
      await userStore.createUserCart({
        userId: this.paymentUserId,
        paymentItems: allItems,
        addressId: shippingAddressId,
      })
      return { error: null }
    },
    parseDynamicForm({ dynamicForm }) {
      return dynamicForm.reduce((result, item) => {
        result[item.id] = item.value
        if (item.value?.hasOwnProperty('isSelected') && !item.value.isSelected) result[item.id] = { ...item.value, additionalResponse: null }
        return result
      }, {})
    },
    async revokeAccess({ organizationId, projectId, email }) {
      try {
        // Validaciones
        if (!organizationId || !projectId || !email) {
          console.error('OrganizationId, projectId and email are required')
          throw new Error('Missing required parameters')
        }
        if (!this.project?.allowedEmails) {
          console.error('Project or allowedEmails not found')
          throw new Error('Project or allowedEmails not found')
        }

        if (!this.project?.allowedEmails?.includes(email)) {
          console.error('Email not found in allowed list')
          throw new Error('Email not found in allowed list')
        }

        const allowedEmails = this.project.allowedEmails.filter((e) => e !== email)
        const projectRef = doc(db, 'properties', organizationId, 'projects', projectId)

        // Actualizar allowedEmails
        await updateDoc(projectRef, { allowedEmails })

        // Crear log en subcolección
        const logRef = doc(collection(db, 'properties', organizationId, 'projects', projectId, 'logs'))
        await setDoc(logRef, {
          type: 'access_revoke',
          organizationId,
          projectId,
          email,
          createdAt: Timestamp.now(),
          previousAllowedEmails: this.project.allowedEmails,
          currentAllowedEmails: allowedEmails,
        })

        return {
          success: true,
          message: `Access successfully revoked for ${email}`,
        }
      } catch (error) {
        console.error('Error revoking access:', error)
        throw new Error(`Failed to revoke access: ${error.message}`)
      }
    },
  },
})
